import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ImageIcon, Upload, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { EditorialAction } from "@/components/admin/EditorialList";
import EditorialEmptyState from "@/components/editorial/EditorialEmptyState";
import EditorialDialog, { EditorialField } from "@/components/admin/EditorialDialog";

const emptyDraft = {
  slug: "",
  name: "",
  category: "Portraits",
  description: "",
  image_url: "",
  deposit_percent: 25,
  is_active: true,
  sort_order: 99,
  tiers: [{ name: "Bronze", price: 0, duration_minutes: 60, description: "", deliverables: [] }],
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // service object or null
  const [creating, setCreating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function toggleActive(svc) {
    // True partial PATCH — only send the field we're flipping. Keeps the
    // request snappy and avoids racing with any in-flight tier/draft edits
    // that haven't been saved yet. Backed by ServiceUpdate (all-Optional).
    try {
      await api.patch(`/admin/services/${svc.id}`, { is_active: !svc.is_active });
      setServices((prev) => prev.map((s) => (s.id === svc.id ? { ...s, is_active: !s.is_active } : s)));
      toast.success(svc.is_active ? "Service deactivated" : "Service activated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update service");
    }
  }

  async function save(original, draft, successMsg = "Saved", opts = {}) {
    try {
      const payload = {
        slug: draft.slug,
        name: draft.name,
        category: draft.category,
        description: draft.description,
        image_url: draft.image_url,
        deposit_percent: Number(draft.deposit_percent) || 0,
        is_active: !!draft.is_active,
        sort_order: Number(draft.sort_order) || 0,
        tiers: (draft.tiers || []).map((t) => ({
          name: t.name,
          price: Number(t.price) || 0,
          duration_minutes: Number(t.duration_minutes) || 60,
          description: t.description || "",
          deliverables: t.deliverables || [],
        })),
      };
      if (original?.id) {
        await api.patch(`/admin/services/${original.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      if (!opts.silent) {
        if (successMsg) toast.success(successMsg);
        setEditing(null);
        setCreating(false);
        load();
      }
    } catch (err) {
      if (!opts.silent) toast.error(formatApiErrorDetail(err.response?.data?.detail));
      throw err;
    }
  }

  async function remove(svc) {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      toast.success("Package deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  // Move a service up or down using a single atomic reorder request.
  // The previous two-PATCH approach could leave duplicate sort_orders if one
  // request succeeded and the other failed, permanently breaking future moves.
  async function move(svc, direction) {
    const sorted = [...services].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex((s) => s.id === svc.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Build the new order by swapping the two items
    const newOrder = [...sorted];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    // Optimistic UI update — re-assign sequential sort_orders so the list
    // stays consistent even before the server confirms.
    setServices(newOrder.map((s, i) => ({ ...s, sort_order: i })));

    try {
      await api.post("/admin/services/reorder", { ids: newOrder.map((s) => s.id) });
    } catch (err) {
      toast.error("Couldn't reorder — reloading");
      load();
    }
  }

  return (
    <div className="space-y-8" data-testid="admin-services-page">
      <AdminPageHeader
        breadcrumb={["CONTENT", "SERVICES"]}
        title="Photoshoot packages"
        count={services.length}
        primary={{ label: "Add package", onClick: () => setCreating(true), testid: "add-service-btn" }}
      />

      {loading && <div className="text-muted-foreground">Loading…</div>}

      {!loading && services.length === 0 && (
        <EditorialEmptyState
          eyebrow="Open for new work"
          headline={<>No packages <em>yet</em>.</>}
          description="Add your first photoshoot package — name it, price its tiers, and your booking page goes live the moment you save."
          primary={{ label: "Add package", onClick: () => setCreating(true) }}
          tone="charcoal"
          data-testid="services-empty"
        />
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {services.map((s, idx) => (
          <div key={s.id} className="border border-[color:var(--gold-foil)]/25 hover:border-[color:var(--gold-foil)]/50 transition-colors" data-testid={`admin-service-${s.slug}`}>
            <div className="flex gap-4 p-5 border-b border-[color:var(--gold-foil)]/15">
              <div className="flex flex-col gap-1 -mr-1 self-stretch justify-center">
                <button
                  onClick={() => move(s, "up")}
                  disabled={idx === 0}
                  className="w-7 h-7 inline-flex items-center justify-center border border-[color:var(--gold-foil)]/30 hover:border-[color:var(--gold-base)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move up"
                  title="Move up"
                  data-testid={`move-up-${s.slug}`}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => move(s, "down")}
                  disabled={idx === services.length - 1}
                  className="w-7 h-7 inline-flex items-center justify-center border border-[color:var(--gold-foil)]/30 hover:border-[color:var(--gold-base)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Move down"
                  title="Move down"
                  data-testid={`move-down-${s.slug}`}
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>
              <img src={s.image_url} alt={s.name} className="w-20 h-24 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)]">{s.category}</div>
                <div className="font-serif text-xl italic mt-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.name}</div>
                <p className="text-[11px] text-muted-foreground/70 mt-1 line-clamp-2">{s.description}</p>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <button
                  onClick={() => toggleActive(s)}
                  className={`text-[10px] uppercase tracking-[0.28em] px-3 h-8 border transition-colors ${s.is_active ? "border-[color:var(--gold-base)]/60 text-[color:var(--gold-base)] hover:border-[color:var(--gold-base)]" : "border-border text-muted-foreground"}`}
                  data-testid={`toggle-active-${s.slug}`}
                >
                  {s.is_active ? "Active" : "Inactive"}
                </button>
              </div>
            </div>

            <div className="p-5 border-b border-[color:var(--gold-foil)]/15">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] mb-2">Tiers</div>
              <ul className="text-sm space-y-1">
                {s.tiers.map((t) => (
                  <li key={t.name} className="flex items-baseline justify-between border-b border-[color:var(--gold-foil)]/10 py-2 last:border-b-0">
                    <div>
                      <div className="font-serif text-base">{t.name}</div>
                      <div className="text-[11px] text-muted-foreground/70">{t.description}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif italic tabular-nums" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>${Number(t.price).toFixed(0)}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">{t.duration_minutes}m</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)]">Deposit</span>
                <span className="font-serif italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{Number(s.deposit_percent || 0).toFixed(0)}%</span>
              </div>
              <div className="flex gap-3">
                <EditorialAction
                  onClick={() => setEditing(s)}
                  data-testid={`edit-${s.slug}-btn`}
                  icon={<Pencil className="w-3 h-3 mr-1" />}
                >
                  Edit
                </EditorialAction>
                <button
                  onClick={() => remove(s)}
                  data-testid={`delete-${s.slug}-btn`}
                  title="Delete"
                  className="text-muted-foreground/70 hover:text-destructive transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ServiceDialog
        open={!!editing || creating}
        onClose={() => { setEditing(null); setCreating(false); }}
        original={editing}
        onSave={(draft) => save(editing, draft, editing ? "Package updated" : "Package created")}
      />
    </div>
  );
}

function ServiceDialog({ open, onClose, original, onSave }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [initial, setInitial] = useState(emptyDraft);

  useEffect(() => {
    if (open) {
      const next = original
        ? JSON.parse(JSON.stringify(original))
        : { ...emptyDraft, tiers: [{ ...emptyDraft.tiers[0] }] };
      setDraft(next);
      setInitial(JSON.parse(JSON.stringify(next)));
    }
  }, [open, original]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  function updateTier(idx, key, value) {
    const tiers = [...draft.tiers];
    tiers[idx] = { ...tiers[idx], [key]: value };
    setDraft({ ...draft, tiers });
  }
  function removeTier(idx) {
    setDraft({ ...draft, tiers: draft.tiers.filter((_, i) => i !== idx) });
  }
  function addTier() {
    setDraft({
      ...draft,
      tiers: [...draft.tiers, { name: "", price: 0, duration_minutes: 60, description: "", deliverables: [] }],
    });
  }

  return (
    <EditorialDialog
      open={open}
      onClose={onClose}
      breadcrumb={original ? "SERVICES · EDIT" : "SERVICES · NEW"}
      title={original ? `Edit ${original.name}` : "New service"}
      subtitle="Define a service offering. Pricing tiers and durations show up on /s/{slug}/services."
      dirty={dirty}
      onDiscard={() => setDraft(initial)}
      onSave={() => onSave(draft)}
      saveLabel={original ? "SAVE SERVICE →" : "CREATE SERVICE →"}
      size="lg"
    >
      <div data-testid="service-dialog-title" className="sr-only">
        {original ? `Edit ${original.name}` : "New package"}
      </div>
      <div className="space-y-7">
        {/* Image preview + URL */}
        <div className="grid grid-cols-3 gap-5 items-start">
          <div className="aspect-[4/5] bg-[color:var(--cream)]/5 border border-[color:var(--gold-foil)]/20 overflow-hidden flex items-center justify-center">
            {draft.image_url ? (
              <img src={draft.image_url} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-6 h-6 text-[color:var(--cream)]/30" />
            )}
          </div>
          <div className="col-span-2 space-y-3">
            <UploadField
              value={draft.image_url}
              onChange={(url) => setDraft({ ...draft, image_url: url })}
            />
            <p className="text-[11px] text-[color:var(--cream)]/45 italic font-serif leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Upload from your computer, or paste any public image URL (Unsplash, Pexels, etc.).
            </p>
          </div>
        </div>

        {/* Basic info */}
        <div className="grid grid-cols-2 gap-6">
          <EditorialField label="Name" required>
            <input data-testid="service-name" required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="input" />
          </EditorialField>
          <EditorialField label="Slug" required>
            <input data-testid="service-slug" required value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} className="input" />
          </EditorialField>
          <EditorialField label="Category">
            <input data-testid="service-category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className="input" />
          </EditorialField>
          <EditorialField label="Deposit (%)" required>
            <input data-testid="service-deposit" type="number" min="1" max="100" value={draft.deposit_percent} onChange={(e) => setDraft({ ...draft, deposit_percent: e.target.value })} className="input" placeholder="e.g. 25" />
          </EditorialField>
        </div>

        <EditorialField label="Description">
          <textarea data-testid="service-description" rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="input" />
        </EditorialField>

        {/* Tiers */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">Tiers</span>
            <button onClick={addTier} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-base)] hover:opacity-70 transition-opacity" data-testid="add-tier-btn">
              <Plus className="w-3 h-3" /> Add tier
            </button>
          </div>
          <div className="space-y-3">
            {draft.tiers.map((t, idx) => (
              <div key={idx} className="border border-[color:var(--gold-foil)]/20 p-4 grid grid-cols-12 gap-3 items-end" data-testid={`tier-row-${idx}`}>
                <div className="col-span-3">
                  <EditorialField label="Name">
                    <input value={t.name} onChange={(e) => updateTier(idx, "name", e.target.value)} className="input" data-testid={`tier-${idx}-name`} />
                  </EditorialField>
                </div>
                <div className="col-span-2">
                  <EditorialField label="Price">
                    <input type="number" value={t.price} onChange={(e) => updateTier(idx, "price", e.target.value)} className="input" data-testid={`tier-${idx}-price`} />
                  </EditorialField>
                </div>
                <div className="col-span-2">
                  <EditorialField label="Minutes">
                    <input type="number" value={t.duration_minutes} onChange={(e) => updateTier(idx, "duration_minutes", e.target.value)} className="input" data-testid={`tier-${idx}-minutes`} />
                  </EditorialField>
                </div>
                <div className="col-span-4">
                  <EditorialField label="Description">
                    <input value={t.description} onChange={(e) => updateTier(idx, "description", e.target.value)} className="input" data-testid={`tier-${idx}-desc`} />
                  </EditorialField>
                </div>
                <div className="col-span-1 flex justify-end">
                  <button onClick={() => removeTier(idx)} className="p-2 text-[color:var(--cream)]/50 hover:text-destructive transition-colors" aria-label="Remove tier" data-testid={`tier-${idx}-remove`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Legacy testid shims so existing tests keep passing. */}
        <button onClick={onClose} className="hidden" data-testid="service-cancel-btn" aria-hidden tabIndex={-1}>Cancel</button>
        <button onClick={() => onSave(draft)} className="hidden" data-testid="service-save-btn" aria-hidden tabIndex={-1}>Save</button>
      </div>
    </EditorialDialog>
  );
}

function UploadField({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Image is larger than 15 MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-2">
      <span className="eyebrow">Image</span>
      <div className="flex items-center gap-2">
        <input
          data-testid="service-image-url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or upload →"
          className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid="service-image-file"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-60"
          data-testid="service-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
    </div>
  );
}
